import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Meteor } from 'meteor/meteor';
import { useTracker } from 'meteor/react-meteor-data';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { toastOnError } from '/imports/ui/utils';
import { useFindOne } from '/imports/hooks';
import { Orgs } from '/imports/api/orgs';
import { DateInput } from '/imports/ui/DateInput';
import { generateCalpineBills } from '/imports/api/calpineBilling';
import { Loading } from '/imports/ui/Loading';
import { getSolarBillsBatchStatus } from '/imports/api/solarBills';

const CalpineBillsPageStyled = styled(Page)`
  .generated {
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
  }
`;

/** Super-user page to generate Calpine solar bills over a date range. */
export const CalpineBillsPage = () => {
  const currentUser = useTracker(() => Meteor.user());
  if (!currentUser?.isSuperUser) return <NotPermittedPage />;
  const orgId = currentUser.orgId;
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [error,] = useState<any>({});
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState({ total: 0, generated: 0 });
  const navigate = useNavigate();
  const generateBills = toastOnError(async () => {
    if (!startDate || !endDate) return;
    setGenerating(true);
    const batch = Date.now();
    const interval = setInterval(async () => {
      setStatus(await getSolarBillsBatchStatus.call({ batch }));
    }, 1500);
    await generateCalpineBills.call({ batch, startDate, endDate });
    clearInterval(interval);
    navigate(`/solar-bills?batch=${batch}`);
    setGenerating(false);
  });
  return (
    <CalpineBillsPageStyled>
      <PageHeading
        left={<h1>{org.name}</h1>}
      />
      <PageBody>
        <Form onSubmit={() => generateBills()}>
          <div className="field-group">
            <FormField field="startDate" error={error}>
              <label>{strings.StartDate()}</label>
              <DateInput value={startDate} onChange={(startDate) => setStartDate(startDate)} />
            </FormField>
            <FormField field="endDate" error={error}>
              <label>{strings.EndDate()}</label>
              <DateInput value={endDate} onChange={(endDate) => setEndDate(endDate)} />
            </FormField>
          </div>
          <div className="field-group">
            <Button type="submit" disabled={!startDate || !endDate}>{generating ? <Loading /> : strings.Submit()}</Button>
            {generating && status.total ? <span className="generated">{strings.Generated()} {status.generated} / {status.total}</span> : null}
          </div>
        </Form>
      </PageBody>
    </CalpineBillsPageStyled>
  );
};
